import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { defaultsDeep } from "lodash";
import { forkJoin, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";

interface Device {
  code: string;
  name: string;
  selected: boolean;
  class: string;
  icon: string;
  swapContainerOpen: boolean;
  newContainerOpen: boolean;
  locationAllowedOptions: any;
  installsOptions: any;
  notificationsAllowedOptions: any;
  activeOptions: any;
  data: any;
}

const DEFUALT_PIE_CHART_OPTIONS = {
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },

  tooltips: { enabled: false },
  hover: { mode: null },

  series: [
    {
      type: "pie",
      radius: ["50%", "80%"],

      labelLine: {
        show: false,
      },
    },
  ],
};

@Component({
  selector: "app-device-info",
  templateUrl: "./device-info.component.html",
  styleUrls: ["./device-info.component.scss"],
})
export class DeviceInfoComponent implements OnInit {
  devices: Device[] = [
    {
      code: "ios",
      name: "iOS",
      selected: true,
      class: "ios-container",
      icon: "ios",
      swapContainerOpen: false,
      newContainerOpen: false,
      locationAllowedOptions: {},
      installsOptions: {},
      notificationsAllowedOptions: {},
      activeOptions: {},
      data: {},
    },
    {
      code: "android",
      name: "Android",
      selected: true,
      class: "android-container",
      icon: "android",
      swapContainerOpen: false,
      newContainerOpen: false,
      locationAllowedOptions: {},
      installsOptions: {},
      notificationsAllowedOptions: {},
      activeOptions: {},
      data: {},
    },
  ];
  filterForm!: FormGroup;
  iosInstallsOptions: any;
  iosActiveOptions: any;
  iosNotificationsAllowedOptions: any;
  iosLocationAllowedOptions: any;
  deviceForm!: FormGroup;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private AppService: AppService,
    private SessionService: SessionService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start, [
        Validators.required,
      ]),
      endDate: new FormControl(this.route.snapshot.data.end, [
        Validators.required,
      ]),
    });

    this.deviceForm = new FormGroup({
      swapDevice: new FormControl(),
      newDevice: new FormControl(),
    });

    this.initDeviceData(
      this.route.snapshot.data.androidDeviceInfo,
      this.route.snapshot.data.iosDeviceInfo,
    );

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (
          this.filterForm.valid &&
          this.filterForm.controls.startDate.value <
            this.filterForm.controls.endDate.value
        ) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          const iosDeviceInfoObs = this.AppService.getDeviceInfo(
            this.SessionService.currentAccountValue!.accountID,
            this.SessionService.currentAppValue!.id,
            "ios",
            value.startDate,
            this.filterForm.controls.endDate.value,
          );
          const androidDeviceInfoObs = this.AppService.getDeviceInfo(
            this.SessionService.currentAccountValue!.accountID,
            this.SessionService.currentAppValue!.id,
            "android",
            value.startDate,
            this.filterForm.controls.endDate.value,
          );

          this.uiService
            .waitForEvent(forkJoin([iosDeviceInfoObs, androidDeviceInfoObs]))
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(([iosDeviceInfo, androidDeviceInfo]) => {
              this.initDeviceData(androidDeviceInfo, iosDeviceInfo);
            });
        }
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get selectedDevices() {
    return this.devices.filter((device) => device.selected);
  }

  get iosDevice() {
    return this.devices.find((device) => device.code === "ios")!;
  }

  get androidDevice() {
    return this.devices.find((device) => device.code === "android")!;
  }

  get unselectedDevices() {
    return this.devices.filter((device) => !device.selected);
  }

  get math() {
    return Math;
  }

  initDeviceData(androidDeviceInfo: any, iosDeviceInfo: any) {
    this.iosDevice.data = {
      ...iosDeviceInfo,
      app_devices_range: Number.parseInt(iosDeviceInfo.app_devices_range),
      devices_count: Number.parseInt(iosDeviceInfo.devices_count),
      locs_allowed: Number.parseInt(iosDeviceInfo.locs_allowed),
      notifs_allowed: Number.parseInt(iosDeviceInfo.notifs_allowed),
      total_installs: Number.parseInt(iosDeviceInfo.total_installs),
      active: Number.parseInt(iosDeviceInfo.active),
      total_active: Number.parseInt(androidDeviceInfo.total_active),
    };

    this.androidDevice.data = {
      ...androidDeviceInfo,
      app_devices_range: Number.parseInt(androidDeviceInfo.app_devices_range),
      devices_count: Number.parseInt(androidDeviceInfo.devices_count),
      locs_allowed: Number.parseInt(androidDeviceInfo.locs_allowed),
      notifs_allowed: Number.parseInt(androidDeviceInfo.notifs_allowed),
      total_installs: Number.parseInt(androidDeviceInfo.total_installs),
      active: Number.parseInt(androidDeviceInfo.active),
      total_active: Number.parseInt(androidDeviceInfo.total_active),
    };

    this.iosDevice.installsOptions = defaultsDeep(
      {
        series: [
          {
            name: "Installs",
            data: [
              {
                value: this.iosDevice.data.app_devices_range,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#7DC4FD",
                },
              },
              {
                value: this.iosDevice.data.total_installs
                  ? this.iosDevice.data.total_installs -
                    this.iosDevice.data.app_devices_range
                  : 1,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.iosDevice.data.total_installs
                    ? Math.round(
                        (this.iosDevice.data.app_devices_range /
                          this.iosDevice.data.total_installs) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.iosDevice.activeOptions = defaultsDeep(
      {
        series: [
          {
            name: "Active",
            data: [
              {
                value: this.iosDevice.data.active,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#7DC4FD",
                },
              },
              {
                value: this.iosDevice.data.total_active
                  ? this.iosDevice.data.total_active -
                    this.iosDevice.data.active
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.iosDevice.data.total_active
                    ? Math.round(
                        (this.iosDevice.data.active /
                          this.iosDevice.data.total_active) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.iosDevice.notificationsAllowedOptions = defaultsDeep(
      {
        series: [
          {
            name: "Notifications",
            data: [
              {
                value: this.iosDevice.data.notifs_allowed,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#7DC4FD",
                },
              },
              {
                value: this.iosDevice.data.app_devices_range
                  ? this.iosDevice.data.app_devices_range -
                    this.iosDevice.data.notifs_allowed
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.iosDevice.data.app_devices_range
                    ? Math.round(
                        (this.iosDevice.data.notifs_allowed /
                          this.iosDevice.data.app_devices_range) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.iosDevice.locationAllowedOptions = defaultsDeep(
      {
        series: [
          {
            name: "Locations",
            data: [
              {
                value: this.iosDevice.data.locs_allowed,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#7DC4FD",
                },
              },
              {
                value: this.iosDevice.data.app_devices_range
                  ? this.iosDevice.data.app_devices_range -
                    this.iosDevice.data.locs_allowed
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.iosDevice.data.app_devices_range
                    ? Math.round(
                        (this.iosDevice.data.locs_allowed /
                          this.iosDevice.data.app_devices_range) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.androidDevice.installsOptions = defaultsDeep(
      {
        series: [
          {
            name: "Installs",
            data: [
              {
                value: this.androidDevice.data.app_devices_range,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#A1C700",
                },
              },
              {
                value: this.androidDevice.data.total_installs
                  ? this.androidDevice.data.total_installs -
                    this.androidDevice.data.app_devices_range
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.androidDevice.data.total_installs
                    ? Math.round(
                        (this.androidDevice.data.app_devices_range /
                          this.androidDevice.data.total_installs) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.androidDevice.activeOptions = defaultsDeep(
      {
        series: [
          {
            name: "Active",
            data: [
              {
                value: this.androidDevice.data.active,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#A1C700",
                },
              },
              {
                value: this.androidDevice.data.total_installs
                  ? this.androidDevice.data.total_installs -
                    this.androidDevice.data.active
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.androidDevice.data.total_installs
                    ? Math.round(
                        (this.androidDevice.data.active /
                          this.androidDevice.data.total_installs) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.androidDevice.notificationsAllowedOptions = defaultsDeep(
      {
        series: [
          {
            name: "Notifications",
            data: [
              {
                value: this.androidDevice.data.notifs_allowed,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#A1C700",
                },
              },
              {
                value: this.androidDevice.data.app_devices_range
                  ? this.androidDevice.data.app_devices_range -
                    this.androidDevice.data.notifs_allowed
                  : 1,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.androidDevice.data.app_devices_range
                    ? Math.round(
                        (this.androidDevice.data.notifs_allowed /
                          this.androidDevice.data.app_devices_range) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.androidDevice.locationAllowedOptions = defaultsDeep(
      {
        series: [
          {
            name: "Locations",
            data: [
              {
                value: this.androidDevice.data.locs_allowed,
                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#A1C700",
                },
              },
              {
                value: this.androidDevice.data.app_devices_range
                  ? this.androidDevice.data.app_devices_range -
                    this.androidDevice.data.locs_allowed
                  : 1,

                label: {
                  color: "#FFF",
                },
                itemStyle: {
                  color: "#949DA1",
                },
              },
            ],
            label: {
              color: "#FFF",
              fontSize: "25",
              position: "center",
              formatter: () => {
                return `${
                  this.androidDevice.data.app_devices_range
                    ? Math.round(
                        (this.androidDevice.data.locs_allowed /
                          this.androidDevice.data.app_devices_range) *
                          100,
                      )
                    : 0
                }%`;
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );
  }

  onSwapDeviceSelected(currentDevice: any) {
    currentDevice.selected = false;
    currentDevice.swapContainerOpen = false;

    this.deviceForm.controls.swapDevice.value.selected = true;
  }

  onNewDeviceSelected(device: any) {
    this.deviceForm.controls.newDevice.value.selected = true;
    device.newContainerOpen = false;
  }

  closeDevice(device: any) {
    device.selected = false;
    device.swapContainerOpen = false;
    device.newContainerOpen = false;
  }
}
